export const paths = {
  public: {
    home: {
      path: '/',
      getHref: () => '/',
    },
    paymentResult: {
      path: '/payment/result',
      getHref: () => '/payment/result',
    },
    auth: {
      login: {
        path: '/login',
        getHref: (redirectTo?: string) => `/login${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
      },
      register: {
        path: '/register',
        getHref: (redirectTo?: string) =>
          `/register${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
      },
      resetPassword: {
        path: '/reset-password',
        getHref: () => '/reset-password',
      },
      verifyEmail: {
        path: '/verify-email',
        getHref: () => '/verify-email',
      },
      verificationStatus: {
        path: '/verification-status',
        getHref: () => '/verification-status',
      },
      setPassword: {
        path: '/set-password',
        getHref: () => '/set-password',
      },
    },
    legal: {
      privacyPolicy: {
        path: '/privacy-policy',
        getHref: () => '/privacy-policy',
      },
      terms: {
        path: '/terms',
        getHref: () => '/terms',
      },
    },
    support: {
      path: '/support',
      getHref: () => '/support',
    },
    unauthorized: {
      path: '/unauthorized',
      getHref: () => '/unauthorized',
    },
    serverError: {
      path: '/500',
      getHref: () => '/500',
    },
  },

  dashboard: {
    root: {
      path: '/',
      getHref: () => '/',
    },
    roles: {
      permissions: {
        path: 'roles/permissions',
        getHref: () => '/roles/permissions',
      },
    },
    member: {
      selectPlan: {
        path: 'select-plan',
        getHref: () => '/select-plan',
      },
      checkout: {
        path: 'checkout',
        getHref: (planId?: string | number, paymentId?: string | number) => {
          if (paymentId) return `/checkout/${paymentId}`
          if (planId) return `/checkout?plan=${planId}`
          return '/checkout'
        },
      },
      myMembership: {
        path: 'my-membership',
        getHref: () => '/my-membership',
      },
      myPayments: {
        path: 'my-invoices',
        getHref: () => '/my-invoices',
        view: {
          path: ':id/view',
          getHref: (id: string | number) => `/my-payments/${id}/view`,
        },
      },
      myBookings: {
        path: 'my-bookings',
        getHref: () => '/my-bookings',
      },
      myNotes: {
        path: 'my-notes',
        getHref: () => '/my-notes',
      },
      bookClass: {
        path: 'book-class',
        getHref: () => '/book-class',
      },
      myHealthTracking: {
        path: 'my-health-tracking',
        getHref: () => '/my-health-tracking',
      },
    },
    healthTracking: {
      vitals: {
        root: {
          path: 'my-health/vitals',
          getHref: () => '/my-health/vitals',
        },
        create: {
          path: 'my-health/vitals/create',
          getHref: () => '/my-health/vitals/create',
        },
        edit: {
          path: 'my-health/vitals/:id/edit',
          getHref: (id: string | number) => `/my-health/vitals/${id}/edit`,
        },
      },
      measurements: {
        root: {
          path: 'my-health/measurements',
          getHref: () => '/my-health/measurements',
        },
        create: {
          path: 'my-health/measurements/create',
          getHref: () => '/my-health/measurements/create',
        },
        edit: {
          path: 'my-health/measurements/:id/edit',
          getHref: (id: string | number) => `/my-health/measurements/${id}/edit`,
        },
      },
      goals: {
        root: {
          path: 'my-health/goals',
          getHref: () => '/my-health/goals',
        },
        create: {
          path: 'my-health/goals/create',
          getHref: () => '/my-health/goals/create',
        },
        view: {
          path: 'my-health/goals/:id/view',
          getHref: (id: string | number) => `/my-health/goals/${id}/view`,
        },
        edit: {
          path: 'my-health/goals/:id/edit',
          getHref: (id: string | number) => `/my-health/goals/${id}/edit`,
        },
      },
      notes: {
        root: {
          path: 'my-health/notes',
          getHref: () => '/my-health/notes',
        },
        create: {
          path: 'my-health/notes/create',
          getHref: () => '/my-health/notes/create',
        },
        view: {
          path: 'my-health/notes/:id/view',
          getHref: (id: string | number) => `/my-health/notes/${id}/view`,
        },
        edit: {
          path: 'my-health/notes/:id/edit',
          getHref: (id: string | number) => `/my-health/notes/${id}/edit`,
        },
      },
      progressPhotos: {
        root: {
          path: 'my-health/progress-photos',
          getHref: () => '/my-health/progress-photos',
        },
        create: {
          path: 'my-health/progress-photos/create',
          getHref: () => '/my-health/progress-photos/create',
        },
        view: {
          path: 'my-health/progress-photos/:id/view',
          getHref: (id: string | number) => `/my-health/progress-photos/${id}/view`,
        },
        edit: {
          path: 'my-health/progress-photos/:id/edit',
          getHref: (id: string | number) => `/my-health/progress-photos/${id}/edit`,
        },
      },
    },
    profile: {
      root: {
        path: 'profile',
        getHref: () => '/profile',
      },
      security: {
        path: 'profile/security',
        getHref: () => '/profile/security',
      },
      localization: {
        path: 'profile/localization',
        getHref: () => '/profile/localization',
      },
      notifications: {
        path: 'profile/notifications',
        getHref: () => '/profile/notifications',
      },
    },
    support: {
      root: {
        path: 'support',
        getHref: () => '/support',
      },
      help: {
        path: 'help',
        getHref: () => '/help',
      },
      myTickets: {
        path: 'my-tickets',
        getHref: () => '/my-tickets',
      },
      view: {
        path: 'support/:id',
        getHref: (id: string | number) => `/support/${id}`,
      },
    },
    settings: {
      root: {
        path: 'app-settings',
        getHref: () => '/app-settings',
      },
      general: {
        path: 'app-settings',
        getHref: () => '/app-settings',
      },
      branding: {
        path: 'app-settings/branding',
        getHref: () => '/app-settings/branding',
      },
      mailBranding: {
        path: 'app-settings/mail-branding',
        getHref: () => '/app-settings/mail-branding',
      },
      security: {
        path: 'app-settings/security',
        getHref: () => '/app-settings/security',
      },
      payments: {
        path: 'app-settings/payments',
        getHref: () => '/app-settings/payments',
      },
      localization: {
        path: 'app-settings/localization',
        getHref: () => '/app-settings/localization',
      },
      integrations: {
        path: 'app-settings/integrations',
        getHref: () => '/app-settings/integrations',
      },
      notifications: {
        path: 'app-settings/notifications',
        getHref: () => '/app-settings/notifications',
      },
      mail: {
        path: 'app-settings/mail',
        getHref: () => '/app-settings/mail',
      },
      bookings: {
        path: 'app-settings/bookings',
        getHref: () => '/app-settings/bookings',
      },
      bookingReminders: {
        path: 'app-settings/booking-reminders',
        getHref: () => '/app-settings/booking-reminders',
      },
      membership: {
        path: 'app-settings/membership',
        getHref: () => '/app-settings/membership',
      },
      gymOperations: {
        path: 'app-settings/gym-operations',
        getHref: () => '/app-settings/gym-operations',
      },
    },
    bannedUsers: {
      root: {
        path: 'banned-users',
        getHref: () => '/banned-users',
      },
      create: {
        path: 'banned-users/create',
        getHref: () => '/banned-users/create',
      },
      view: {
        path: 'banned-users/:id/view',
        getHref: (id: string | number) => `/banned-users/${id}/view`,
      },
      edit: {
        path: 'banned-users/:id/edit',
        getHref: (id: string | number) => `/banned-users/${id}/edit`,
      },
    },
    deletionRequests: {
      root: {
        path: 'deletion-requests',
        getHref: () => '/deletion-requests',
      },
      create: {
        path: 'deletion-requests/create',
        getHref: () => '/deletion-requests/create',
      },
      view: {
        path: 'deletion-requests/:id/view',
        getHref: (id: string | number) => `/deletion-requests/${id}/view`,
      },
      edit: {
        path: 'deletion-requests/:id/edit',
        getHref: (id: string | number) => `/deletion-requests/${id}/edit`,
      },
    },
    pendingMembers: {
      root: {
        path: 'pending-members',
        getHref: () => '/pending-members',
      },
    },
    members: {
      root: {
        path: 'members',
        getHref: () => '/members',
      },
      create: {
        path: 'members/create',
        getHref: () => '/members/create',
      },
      view: {
        path: 'members/:id/view',
        getHref: (id: string | number) => `/members/${id}/view`,
      },
      edit: {
        path: 'members/:id/edit',
        getHref: (id: string | number) => `/members/${id}/edit`,
      },
      health: {
        vitals: {
          create: {
            path: 'members/:memberId/health/vitals/create',
            getHref: (memberId: string | number) => `/members/${memberId}/health/vitals/create`,
          },
          edit: {
            path: 'members/:memberId/health/vitals/:id/edit',
            getHref: (memberId: string | number, id: string | number) =>
              `/members/${memberId}/health/vitals/${id}/edit`,
          },
        },
        measurements: {
          create: {
            path: 'members/:memberId/health/measurements/create',
            getHref: (memberId: string | number) => `/members/${memberId}/health/measurements/create`,
          },
          edit: {
            path: 'members/:memberId/health/measurements/:id/edit',
            getHref: (memberId: string | number, id: string | number) =>
              `/members/${memberId}/health/measurements/${id}/edit`,
          },
        },
        goals: {
          create: {
            path: 'members/:memberId/health/goals/create',
            getHref: (memberId: string | number) => `/members/${memberId}/health/goals/create`,
          },
          edit: {
            path: 'members/:memberId/health/goals/:id/edit',
            getHref: (memberId: string | number, id: string | number) => `/members/${memberId}/health/goals/${id}/edit`,
          },
        },
      },
    },
    assistants: {
      root: {
        path: 'assistants',
        getHref: () => '/staff/assistants',
      },
      create: {
        path: 'assistants/create',
        getHref: () => '/staff/assistants/create',
      },
      view: {
        path: 'assistants/:id/view',
        getHref: (id: string | number) => `/staff/assistants/${id}/view`,
      },
      edit: {
        path: 'assistants/:id/edit',
        getHref: (id: string | number) => `/staff/assistants/${id}/edit`,
      },
    },
    gymManagers: {
      root: {
        path: 'gym-managers',
        getHref: () => '/staff/gym-managers',
      },
      create: {
        path: 'gym-managers/create',
        getHref: () => '/staff/gym-managers/create',
      },
      view: {
        path: 'gym-managers/:id/view',
        getHref: (id: string | number) => `/staff/gym-managers/${id}/view`,
      },
      edit: {
        path: 'gym-managers/:id/edit',
        getHref: (id: string | number) => `/staff/gym-managers/${id}/edit`,
      },
    },
    coaches: {
      root: {
        path: 'coaches',
        getHref: () => '/staff/coaches',
      },
      create: {
        path: 'coaches/create',
        getHref: () => '/staff/coaches/create',
      },
      view: {
        path: 'coaches/:id/view',
        getHref: (id: string | number) => `/staff/coaches/${id}/view`,
      },
      edit: {
        path: 'coaches/:id/edit',
        getHref: (id: string | number) => `/staff/coaches/${id}/edit`,
      },
    },
    checkIn: {
      root: {
        path: 'checkin',
        getHref: () => '/checkin',
      },
    },
    checkIns: {
      root: {
        path: 'checkins',
        getHref: () => '/checkins',
      },
      view: {
        path: 'checkins/:id/view',
        getHref: (id: string | number) => `/checkins/${id}/view`,
      },
      edit: {
        path: 'checkins/:id/edit',
        getHref: (id: string | number) => `/checkins/${id}/edit`,
      },
      create: {
        path: 'checkins/create',
        getHref: () => '/checkins/create',
      },
    },
    branches: {
      root: {
        path: 'branches',
        getHref: () => '/branches',
      },
      edit: {
        path: 'branches/:id/edit',
        getHref: (id: string | number) => `/branches/${id}/edit`,
      },
      create: {
        path: 'branches/create',
        getHref: () => '/branches/create',
      },
      settings: {
        path: 'branches/:id/settings',
        getHref: (id: string | number) => `/branches/${id}/settings`,
      },
    },
    classTypes: {
      root: {
        path: 'class-types',
        getHref: () => '/class-types',
      },
      view: {
        path: 'class-types/:id/view',
        getHref: (id: string | number) => `/class-types/${id}/view`,
      },
      edit: {
        path: 'class-types/:id/edit',
        getHref: (id: string | number) => `/class-types/${id}/edit`,
      },
      create: {
        path: 'class-types/create',
        getHref: () => '/class-types/create',
      },
      export: {
        path: 'class-types/export',
        getHref: () => '/class-types/export',
      },
    },
    classes: {
      root: {
        path: 'classes',
        getHref: () => '/classes',
      },
      view: {
        path: 'classes/:id/view',
        getHref: (id: string | number) => `/classes/${id}/view`,
      },
      edit: {
        path: 'classes/:id/edit',
        getHref: (id: string | number) => `/classes/${id}/edit`,
      },
      create: {
        path: 'classes/create',
        getHref: () => '/classes/create',
      },
      export: {
        path: 'classes/export',
        getHref: () => '/classes/export',
      },
    },
    classTemplates: {
      root: {
        path: 'class-templates',
        getHref: () => '/class-templates',
      },
      edit: {
        path: 'class-templates/:id/edit',
        getHref: (id: string | number) => `/class-templates/${id}/edit`,
      },
      generate: {
        path: 'class-templates/:id/generate',
        getHref: (id: string | number) => `/class-templates/${id}/generate`,
      },
      create: {
        path: 'class-templates/create',
        getHref: () => '/class-templates/create',
      },
    },
    bookings: {
      root: {
        path: 'bookings',
        getHref: () => '/bookings',
      },
      view: {
        path: 'bookings/:id/view',
        getHref: (id: string | number) => `/bookings/${id}/view`,
      },
      edit: {
        path: 'bookings/:id/edit',
        getHref: (id: string | number) => `/bookings/${id}/edit`,
      },
      create: {
        path: 'bookings/create',
        getHref: () => '/bookings/create',
      },
    },
    plans: {
      root: {
        path: 'plans',
        getHref: () => '/plans',
      },
      edit: {
        path: 'plans/:id/edit',
        getHref: (id: string | number) => `/plans/${id}/edit`,
      },
      create: {
        path: 'plans/create',
        getHref: () => '/plans/create',
      },
    },
    invoices: {
      root: {
        path: 'invoices',
        getHref: () => '/invoices',
      },
      view: {
        path: 'invoices/:id/view',
        getHref: (id: string | number) => `/invoices/${id}/view`,
      },
    },
    myPayments: {
      root: {
        path: 'my-invoices',
        getHref: () => '/my-invoices',
      },
      view: {
        path: 'my-invoices/:id/view',
        getHref: (id: string | number) => `/my-invoices/${id}/view`,
      },
    },
    auditLogs: {
      root: {
        path: 'audit-logs',
        getHref: () => '/audit-logs',
      },
    },
    apiHealth: {
      root: {
        path: 'api-health',
        getHref: () => '/api-health',
      },
    },
    reports: {
      root: {
        path: '/reports',
        getHref: () => '/reports',
      },
      attendance: {
        path: '/reports/attendance',
        getHref: () => '/reports/attendance',
      },
      membership: {
        path: '/reports/memberships',
        getHref: () => '/reports/memberships',
      },
      bookings: {
        path: '/reports/bookings',
        getHref: () => '/reports/bookings',
      },
      financial: {
        path: '/reports/financial',
        getHref: () => '/reports/financial',
      },
    },
  },

  notFound: {
    path: '*',
    getHref: () => '/not-found',
  },
} as const
